/**
 * The streaming-create session, decoupled from the URL that delivered it.
 *
 * A streaming create redirects from /workshop to
 * `/project/:projectId/:packetId?stream_id&last_event_id&total_activities`, and
 * ProjectContext resumes the SSE stream from those params.
 *
 * Reading them reactively made the stream's lifetime a function of the query
 * string, and the query string churns for reasons that have nothing to do with
 * streaming — a tab switch, exiting a version view, the multi-export OAuth
 * return. A churn that dropped `stream_id` looked exactly like "the stream
 * ended": the effect's cleanup aborted the connection and the re-run bailed with
 * nothing to reconnect to. Every remaining activity and image event for the run
 * was lost (there is one SSE stream for the whole generation, primary and
 * secondaries both), and `isStreaming` stuck true, which in turn blocked the
 * pending-image backfill that would otherwise have healed it.
 *
 * So the session is LATCHED: read once when the params appear, kept until the
 * project changes or a genuinely different stream starts. The URL still carries
 * the params while we're on the streamed primary — that is what lets a reload
 * resume — but it no longer decides whether the stream is alive.
 */

/**
 * The params that describe a resumable stream. They are meaningful only next to
 * the primary packet's id in the path — see withoutStreamParams.
 */
export const STREAM_PARAM_KEYS = ['stream_id', 'last_event_id', 'total_activities'];

/** Parse a resumable session out of the URL, or null if the URL doesn't carry one. */
export function deriveStreamSession(searchParams) {
  const streamId = searchParams.get('stream_id');
  const lastEventId = searchParams.get('last_event_id');
  if (!streamId || !lastEventId) return null;
  // A version snapshot wins over a live stream — loadProject already warns that it
  // ignores stream_id when ?version is set, but the resume used to start anyway and
  // then overwrite the snapshot with live data on its first refresh.
  if (searchParams.get('version')) return null;
  return {
    streamId,
    lastEventId,
    totalActivities: parseInt(searchParams.get('total_activities'), 10) || 0,
  };
}

/**
 * Decide which session should be running, given the latched one (`current`, a
 * `{ projectId, session }` pair), what the URL says now (`next`), and the
 * project we're rendering.
 *
 * Returns `current` itself whenever nothing should change, so the `session`
 * object identity stays stable and the resume effect doesn't restart.
 */
export function latchStreamSession(current, next, projectId) {
  // A session belonging to another project isn't ours to keep. Dropping it is
  // also what lets the resume effect clear isStreaming for the project we just
  // walked into (previously it stayed true and mis-stamped that project's load).
  if (current.projectId !== projectId) return { projectId, session: next };
  // The URL no longer carries params — a tab switch, a version exit, or
  // finishStream's own strip. None of those end the stream.
  if (!next) return current;
  // Same stream, re-derived from a new searchParams instance: keep the latched
  // object so the effect sees no dependency change.
  if (next.streamId === current.session?.streamId) return current;
  // A genuinely different stream started for this project.
  return { projectId, session: next };
}

/** A packet URL carrying the given query string verbatim. */
export function packetUrlWithParams(projectId, packetId, searchParams) {
  const search = searchParams?.toString() || '';
  return `/project/${projectId}/${packetId}${search ? `?${search}` : ''}`;
}

/**
 * A copy of the query string with the stream params removed. Use it for any
 * navigation whose target is NOT the packet the stream is building.
 *
 * They have to go. Both producers of a streaming URL — the /workshop redirect and
 * the MCP connector's "watch it build" link — put the **primary** packet's id in
 * the path, and the resume effect reads that path (`urlPacketId`) to decide which
 * packet the stream is building. Carry the params onto a secondary and a reload
 * makes the *secondary* the stream target: the real primary falls into the
 * `_isLoadingSecondary` branch and renders as a non-clickable "generating…" pill
 * for the rest of the run, and the secondary gets padded with the primary's
 * loading placeholders.
 *
 * Dropping them only costs reload-resume after a tab switch, which falls back to
 * the on-load image backfill. The latch is what keeps the live stream alive, so the
 * connection itself survives the switch either way.
 *
 * Everything else rides along — notably `?version`, which a tab switch used to drop,
 * silently dumping the teacher out of a version view and back onto live data.
 */
export function withoutStreamParams(searchParams) {
  const next = new URLSearchParams(searchParams || '');
  STREAM_PARAM_KEYS.forEach((key) => next.delete(key));
  return next;
}
